import cryptoRandomString from "crypto-random-string";
import moment from "moment";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import generalOptionSets from "../../options/general.json";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleAddFieldTableRow,
  handleCopyUrl,
  handleDeleteFieldTableRow,
  handleEditFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  handleSaveFieldTableRow,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldText,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FieldTable,
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormSelect,
  FormTextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import {
  getCareModalities,
  getClientIdentifiers,
  getDiagnosisDetails,
  getMedications,
  getRecordedServicesPromise,
} from "../services/soar";
import { getSchema } from "./joi/validation";

function OHTFCSORCMonthlyProgressReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return getFieldValue(data.SelectedProgram);
  }, [data]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow, unshift = false) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow, unshift);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [loadStatus, setLoadStatus] = useState({});
  const [controllerSignal, setControllerSignal] = useState({});
  const [careModalities, setCareModalities] = useState([]);
  const [recordedServices, setRecordedServices] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  const clientRecordsLoaded = useMemo(() => {
    return loadStatus.clientRecordsLoaded;
  }, [loadStatus.clientRecordsLoaded]);

  const initialRecordsProcessed = useMemo(() => {
    return loadStatus.initialRecordsProcessed;
  }, [loadStatus.initialRecordsProcessed]);

  const clientRecordsProcessed = useMemo(() => {
    return loadStatus.clientRecordsProcessed;
  }, [loadStatus.clientRecordsProcessed]);

  const dxRecordsProcessed = useMemo(() => {
    return loadStatus.dxRecordsProcessed;
  }, [loadStatus.dxRecordsProcessed]);

  useEffect(() => {
    if (
      clientid &&
      clientData?.programs &&
      !formDisabled &&
      !clientRecordsLoaded
    ) {
      const loadRecords = async () => {
        const results = await Promise.all([
          getRecordedServicesPromise(clientid, controllerSignal),
          getCareModalities(clientid, controllerSignal),
        ]);

        console.group("loadRecords");
        if (!controllerSignal.aborted) {
          const activeSignedServices = results[0].filter((service) => {
            const serviceItem = getFieldName(service.ccx_serviceitem);

            return (
              getFieldName(service.ccx_esignature) === "Yes" &&
              serviceItem !== "Daily Attendance"
            );
          });
          setRecordedServices(activeSignedServices);
          console.log("activeSignedServices:", activeSignedServices);

          const sortedModalities = results[1].sort((a, b) => {
            const aStart = moment(getFieldText(a.ccx_startdate));
            const bStart = moment(getFieldText(b.ccx_startdate));

            if (aStart.isAfter(bStart)) return 1;
            if (bStart.isAfter(aStart)) return -1;
            return 0;
          });
          setCareModalities(sortedModalities);
          console.log("sortedModalities", sortedModalities);

          setData((d) => {
            const obj = { ...d };

            if (sortedModalities.length > 0) {
              const selectedProgram = clientData.programs.filter(
                (option) =>
                  option.ccx_client_programid ===
                  getFieldValue(obj.SelectedProgram)
              )[0];
              console.log("selectedProgram", selectedProgram);

              if (!selectedProgram) {
                return obj;
              }

              const modality = sortedModalities.filter((modality) =>
                getFieldName(modality.ccx_clientprogramid).includes(
                  selectedProgram.ccx_name.replace(" - TFC", "")
                )
              )[0];
              console.log("modality:", modality);

              if (modality !== undefined) {
                obj.LOC["@value"] = getFieldName(modality.ccx_caremodality);
              }
            }

            return obj;
          });
          setLoadStatus((ls) => {
            return { ...ls, clientRecordsLoaded: true };
          });
        }
        console.groupEnd();
      };

      loadRecords();
    }
  }, [
    formDisabled,
    clientRecordsLoaded,
    clientData,
    clientid,
    controllerSignal,
  ]);

  useEffect(() => {
    if (!initialProgram?.ccx_client_programid && !initialRecordsProcessed) {
      setLoadStatus((status) => {
        return { ...status, initialRecordsProcessed: true };
      });
    } else if (
      !formDisabled &&
      !initialRecordsProcessed &&
      clientRecordsLoaded &&
      safyInfo?.user?.displayName !== undefined &&
      selectedProgramId !== undefined &&
      careTeams.length > 0
    ) {
      const programCareTeam = careTeams.filter(
        (item) => getFieldText(item.ccx_clientprogram) === selectedProgramId
      );
      const programFosterParents = programCareTeam.filter(
        (item) => getFieldName(item.sor_contactrole) === "Foster Parent"
      );
      const programTherapist = programCareTeam.filter(
        (item) => getFieldName(item.sor_providerrole) === "Therapist"
      )[0];

      setData((d) => {
        const obj = { ...d };
        obj.FormCompletedBy["@value"] = safyInfo.user.displayName;
        obj.FPName["@value"] = programFosterParents
          .map((fp) => fp["con.firstlast"])
          .join(",  ");
        obj.PrimaryCaretaker["@value"] =
          programFosterParents[0]?.["con.firstlast"] || "";
        obj.SecondaryCaretaker["@value"] =
          programFosterParents[1]?.["con.firstlast"] || "";
        obj.TherapistName["@value"] =
          programTherapist?.["user.firstlast"] || "";

        if (careTeams.length > 0) {
          obj.AgencyName["@value"] = "SAFY";
        }

        return obj;
      });

      setLoadStatus((status) => {
        return { ...status, initialRecordsProcessed: true };
      });
    }
  }, [
    initialProgram,
    initialRecordsProcessed,
    formDisabled,
    clientRecordsLoaded,
    careTeams,
    selectedProgramId,
    safyInfo,
  ]);

  useEffect(() => {
    if (!initialProgram?.ccx_client_programid && !clientRecordsProcessed) {
      setLoadStatus((status) => {
        return { ...status, clientRecordsProcessed: true };
      });
    } else if (!clientRecordsProcessed && initialRecordsProcessed && clientid) {
      const loadRecords = async () => {
        const results = await Promise.all([
          getMedications(clientid, controllerSignal),
          getClientIdentifiers(clientid, controllerSignal),
        ]);

        if (controllerSignal.aborted) {
          console.log("initial loading aborted");
        } else if (results?.length === 4) {
          console.group("Initial Processing");
          console.log("results:", results);

          const currentMedications = results[0].filter(
            (med) =>
              med.ccx_stopdate === undefined ||
              moment(getFieldText(med.ccx_stopdate)).isSameOrAfter(moment())
          );
          console.log("currentMedications:", currentMedications);

          const sacwis = results[1].filter(
            (identifier) =>
              identifier.ccx_clientidentifiertype["@name"] === "SACWIS ID"
          )[0];
          console.log("sacwis:", sacwis);

          setData((d) => {
            const obj = { ...d };

            if (currentMedications?.length > 0) {
              obj.CurrentMedications["@value"] = currentMedications
                .map((med) => med.ccx_name)
                .join(", ");
              console.log(
                "obj.CurrentMedications:",
                obj.CurrentMedications["@value"]
              );
            }

            if (sacwis) {
              obj.SACWISID["@value"] = sacwis.ccx_name;
              console.log("obj.SACWISID:", obj.SACWISID["@value"]);
            }

            console.groupEnd();
            return obj;
          });
          setLoadStatus((status) => {
            return { ...status, clientRecordsProcessed: true };
          });
        } else {
          setLoadStatus((status) => {
            return { ...status, clientRecordsProcessed: true };
          });
        }
      };

      loadRecords();
    }
  }, [
    initialProgram,
    clientid,
    clientData,
    initialRecordsProcessed,
    clientRecordsProcessed,
    controllerSignal,
  ]);

  const getDxDetails = useCallback(
    async (programId) => {
      const clientDx = await getDiagnosisDetails(programId, controllerSignal);

      if (!controllerSignal.aborted && clientDx) {
        setData((d) => {
          const obj = { ...d };
          obj.CurrentDiagnosis["@value"] = clientDx
            .map((dx) => `${dx["dx.ccx_code"]} - ${dx["dx.ccx_name"]}`)
            .join("; ");
          return obj;
        });
        setLoadStatus((ls) => {
          return { ...ls, dxRecordsProcessed: true };
        });
      }
    },
    [controllerSignal]
  );

  useEffect(() => {
    if (!initialProgram?.ccx_client_programid && !dxRecordsProcessed) {
      setLoadStatus((status) => {
        return { ...status, dxRecordsProcessed: true };
      });
    } else if (!dxRecordsProcessed && clientRecordsProcessed) {
      getDxDetails(initialProgram.ccx_client_programid);
    }
  }, [
    initialProgram,
    clientRecordsProcessed,
    dxRecordsProcessed,
    getDxDetails,
  ]);

  const processLevelOfCare = useCallback(() => {
    console.group("processLOC");
    setData((d) => {
      const obj = { ...d };

      if (!getFieldValue(obj.SelectedProgram)) {
        return obj;
      }

      if (careModalities.length === 0) {
        delete obj.LOC["@value"];
      } else {
        console.log("careModalities", careModalities);
        console.log("clientData", clientData);
        const selectedProgram = clientData.programs.filter(
          (option) =>
            option.ccx_client_programid === getFieldValue(obj.SelectedProgram)
        )[0];
        console.log("selectedProgram", selectedProgram);

        if (selectedProgram?.ccx_name.includes("TFC")) {
          const selectedProgramFacilityName = selectedProgram.ccx_name.replace(
            "TFC",
            "Foster Homes"
          );
          console.log(
            "selectedProgramFacilityName",
            selectedProgramFacilityName
          );

          const fosterProgram = clientData.programs.filter(
            (option) =>
              option.ccx_name === selectedProgramFacilityName &&
              getFieldDate(option.ccx_admitdate) ===
                getFieldDate(selectedProgram.ccx_admitdate)
          )[0];
          console.log("fosterProgram", fosterProgram);

          if (fosterProgram) {
            const modality = careModalities.filter((modality) => {
              if (
                getFieldText(modality.ccx_clientprogramid) !==
                fosterProgram.ccx_client_programid
              ) {
                return false;
              }

              const reportStart = moment(obj.Date["@start"]);
              const reportEnd = moment(obj.Date["@end"]);
              const modalityStart = moment(
                getFieldDate(modality.ccx_startdate)
              );
              const modalityEnd = moment(getFieldDate(modality.ccx_enddate));

              if (
                reportStart.isSameOrAfter(modalityStart) &&
                reportStart.isSameOrBefore(modalityEnd)
              ) {
                return true;
              }

              if (
                reportEnd.isSameOrAfter(modalityStart) &&
                reportEnd.isSameOrBefore(modalityEnd)
              ) {
                return true;
              }

              return false;
            })[0];
            console.log("modality", modality);

            if (!modality?.ccx_caremodality) {
              obj.LOC["@value"] = "no LOC found";
            } else {
              obj.LOC["@value"] = getFieldName(modality.ccx_caremodality);
            }
          }
        }
      }

      return obj;
    });
    console.groupEnd();
  }, [careModalities, clientData]);

  const getDivision = useCallback(() => {
    const selectedProgramInfo = clientData?.programs?.filter(
      (p) => p.ccx_client_programid === selectedProgramId
    )[0];

    return selectedProgramInfo?.["fac.city"] || "";
  }, [selectedProgramId, clientData]);

  const convertRecordedServices = useCallback(() => {
    setData((d) => {
      const obj = { ...d };

      if (!getFieldValue(obj.SelectedProgram)) {
        return obj;
      }

      const startDate = moment(obj.Date["@start"]);
      const endDate = moment(obj.Date["@end"]);
      console.group("Recorded Services");
      console.log("Recorded Services", recordedServices);
      const monthlyRecordedServices = recordedServices.filter((service) => {
        const serviceStartDate = moment(
          getFieldText(service.ccx_starttime)
        ).startOf("day");

        return (
          getFieldText(service.ccx_clientprogram) ===
            getFieldValue(obj.SelectedProgram) &&
          serviceStartDate.isSameOrAfter(startDate) &&
          serviceStartDate.isSameOrBefore(endDate)
        );
      });

      console.log("Monthly Recorded Services", monthlyRecordedServices);
      if (monthlyRecordedServices.length > 0) {
        const monthlyTherapySessions = monthlyRecordedServices.filter(
          (service) => {
            const serviceItem = getFieldName(service.ccx_serviceitem);

            return serviceItem.includes("Psychotherapy");
          }
        );

        if (monthlyTherapySessions.length > 0) {
          obj.TherapyDates["@value"] = monthlyTherapySessions
            .map((service) =>
              moment(getFieldText(service.ccx_starttime)).format("M/D/YYYY")
            )
            .join(", ");
        }

        const monthlyContactServices = monthlyRecordedServices.filter(
          (service) => {
            const serviceItem = getFieldName(service.ccx_serviceitem);

            return (
              serviceItem === "CPST - H0036" ||
              serviceItem.includes("Child Welfare") ||
              serviceItem === "TBS - H2019" ||
              serviceItem.includes("Psychotherapy")
            );
          }
        );

        obj.ContactDetails["@value"] = JSON.stringify(
          monthlyContactServices.map((service) => {
            return {
              _id: cryptoRandomString(10),
              Date: moment(getFieldText(service.ccx_starttime)).format(
                moment.HTML5_FMT.DATE
              ),
              Location: getFieldName(service.ccx_placeofservice),
              Parties: getFieldName(service.ccx_recipientcode),
            };
          })
        );

        const earliestChildWelfareService = monthlyRecordedServices
          .filter((service) => {
            if (
              getFieldName(service.ccx_deliverymethod) !== "Face-to-Face" ||
              getFieldName(service.ccx_placeofservice) !== "Home" ||
              getFieldName(service.ccx_recipientcode) !==
                "Client and Family Members"
            ) {
              return false;
            }

            const serviceItem = getFieldName(service.ccx_serviceitem);
            return serviceItem === "Child Welfare Case Management";
          })
          .sort((a, b) => {
            return (
              new Date(getFieldText(a.ccx_starttime)) -
              new Date(getFieldText(b.ccx_starttime))
            );
          })[0];

        if (earliestChildWelfareService) {
          obj.DateOfHomeVisit["@value"] =
            earliestChildWelfareService.ccx_starttime["@date"];
          obj.TimeOfHomeVisit["@value"] =
            earliestChildWelfareService.ccx_starttime["@time"];
        }
      }

      console.groupEnd();
      return obj;
    });
  }, [recordedServices]);

  const handleDateChange = ({ currentTarget: input }) => {
    setData((d) => {
      const obj = { ...d };

      if (input.name === "StartDate") {
        obj.Date["@start"] = input.value;
        obj.Date["@value"] = input.value;
        obj.Date["@month"] = moment(input.value).month() + 1;
      } else if (input.name === "EndDate") {
        obj.Date["@end"] = input.value;
      } else {
        obj.Date["@month"] = input.value;

        const momentObj = moment();

        if (Number(input.value > momentObj.month() + 1)) {
          momentObj.subtract(1, "year");
        }

        momentObj.month(input.value - 1);

        const startOfMonth = momentObj
          .startOf("month")
          .format(moment.HTML5_FMT.DATE);
        const endOfMonth = momentObj
          .endOf("month")
          .format(moment.HTML5_FMT.DATE);

        obj.Date["@start"] = startOfMonth;
        obj.Date["@end"] = endOfMonth;
        obj.Date["@value"] = startOfMonth;
      }
      return obj;
    });
    convertRecordedServices();
    processLevelOfCare();
  };

  const customValidate = (errs) => {
    const customErrors = {};

    //Vulnerabilities
    if (
      data.VulnerabilitiesNone["@value"] !== "true" &&
      data.VulnerabilitiesCommunication["@value"] !== "true" &&
      data.VulnerabilitiesSelfProtection["@value"] !== "true" &&
      data.VulnerabilitiesAbuse["@value"] !== "true" &&
      data.VulnerabilitiesHelpers["@value"] !== "true" &&
      data.VulnerabilitiesAge["@value"] !== "true" &&
      data.VulnerabilitiesEmotionalNeeds["@value"] !== "true" &&
      data.VulnerabilitiesDevelopmental["@value"] !== "true" &&
      data.VulnerabilitiesAppearance["@value"] !== "true" &&
      data.VulnerabilitiesSpecialNeeds["@value"] !== "true" &&
      data.VulnerabilitiesVictimization["@value"] !== "true" &&
      data.VulnerabilitiesProvocative["@value"] !== "true" &&
      data.VulnerabilitiesResilience["@value"] !== "true" &&
      data.VulnerabilitiesRole["@value"] !== "true" &&
      data.VulnerabilitiesVisibility["@value"] !== "true"
    ) {
      customErrors.Vulnerabilities =
        "One or more Vulnerability options are required.";
    }

    // Caregiver Protective Capacities
    if (
      data.ProtectionNeeds["@value"] !== "true" &&
      data.ProtectionIntervene["@value"] !== "true" &&
      data.ProtectionBond["@value"] !== "true" &&
      data.ProtectionProvides["@value"] !== "true" &&
      data.ProtectionResponds["@value"] !== "true" &&
      data.ProtectionDiscipline["@value"] !== "true" &&
      data.ProtectionPositive["@value"] !== "true"
    ) {
      customErrors.ProtectiveCapacities =
        "One or more Caregiver Protective Capactities are required.";
    }

    const allErrors = { ...errs, ...customErrors };
    console.log("CustomErrors: ", allErrors);
    setErrors(allErrors);
    return allErrors;
  };

  const levelOfCareOptions = useMemo(() => {
    return careModalities.map((modality) => {
      return {
        _id: getFieldName(modality.ccx_caremodality),
        name: getFieldName(modality.ccx_caremodality),
      };
    });
  }, [careModalities]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          customValidate={customValidate}
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="container border-top border-start">
                <div className="row border-bottom bg-light">
                  <div className="col border-end p-2 fw-bold">
                    {data.Section1ALabel["@label"].replace(
                      "|divisionName|",
                      getDivision(data)
                    )}
                  </div>
                </div>
                <div className="d-none d-print-inline row border-bottom">
                  <div className="col-4 border-end p-2 fw-bold">
                    {data.ClientNameLabel["@label"]}
                  </div>
                  <div className="col-4 border-end p-2">
                    {clientData.client?.fullName}
                  </div>
                  <div className="col border-end p-2 fw-bold">
                    {data.Date["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="Date"
                      error={errors.Date}
                      value={getFieldValue(data.Date)}
                      onChange={handleChange}
                      identical={compare("Date")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                </div>
                <div className="d-print-none row border-bottom">
                  <div className="col-3 border-end p-2 fw-bold">
                    {data.ClientNameLabel["@label"]}
                  </div>
                  <div className="col-3 border-end p-2">
                    {clientData.client?.fullName}
                  </div>
                  <div className="col-2 border-end p-2 fw-bold">
                    <FormSelect
                      name="ReportMonth"
                      label="Report Month:"
                      error={errors.Date}
                      value={data.Date["@month"]}
                      options={generalOptionSets.month}
                      onChange={handleDateChange}
                      disabled={formDisabled || !loadStatus.dxRecordsProcessed}
                    />
                  </div>
                  <div className="col-2 border-end p-2">
                    <FormInput
                      name="StartDate"
                      label="Start Date:"
                      value={data.Date["@start"]}
                      onChange={handleDateChange}
                      disabled={formDisabled || !loadStatus.dxRecordsProcessed}
                      type="date"
                    />
                  </div>
                  <div className="col-2 border-end p-2">
                    <FormInput
                      name="EndDate"
                      label="End Date:"
                      value={data.Date["@end"]}
                      onChange={handleDateChange}
                      disabled={formDisabled || !loadStatus.dxRecordsProcessed}
                      type="date"
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end p-2 fw-bold">
                    {data.SACWISID["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="SACWISID"
                      error={errors.SACWISID}
                      value={getFieldValue(data.SACWISID)}
                      onChange={handleChange}
                      identical={compare("SACWISID")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col border-end p-2">
                    <FormSelect
                      name="LOC"
                      label={<b>{data.LOC["@label"]}</b>}
                      error={errors.Date}
                      value={data.LOC["@value"]}
                      options={levelOfCareOptions}
                      onChange={handleChange}
                      disabled={formDisabled || !loadStatus.clientRecordsLoaded}
                    />
                  </div>
                  <div className="col border-end p-2 fw-bold">
                    {data.DOB["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    {clientData.client?.dob["@date"]}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end p-2 fw-bold">
                    {data.PermanencyGoal["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <div className="row">
                      <div className="col">
                        <FormRadioButton
                          name="PermanencyGoal"
                          error={errors.PermanencyGoal}
                          value={getFieldValue(data.PermanencyGoal)}
                          options={[
                            JSON.parse(
                              data.PermanencyGoal["@options"].replaceAll(
                                "'",
                                '"'
                              )
                            )[0],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsOnNewLine={true}
                        />
                      </div>
                      <div className="col">
                        <FormRadioButton
                          name="PermanencyGoal"
                          error={errors.PermanencyGoal}
                          value={getFieldValue(data.PermanencyGoal)}
                          options={[
                            JSON.parse(
                              data.PermanencyGoal["@options"].replaceAll(
                                "'",
                                '"'
                              )
                            )[1],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsOnNewLine={true}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormRadioButton
                          name="PermanencyGoal"
                          error={errors.PermanencyGoal}
                          value={getFieldValue(data.PermanencyGoal)}
                          options={[
                            JSON.parse(
                              data.PermanencyGoal["@options"].replaceAll(
                                "'",
                                '"'
                              )
                            )[2],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsOnNewLine={true}
                        />
                      </div>
                      <div className="col">
                        <FormRadioButton
                          name="PermanencyGoal"
                          error={errors.PermanencyGoal}
                          value={getFieldValue(data.PermanencyGoal)}
                          options={[
                            JSON.parse(
                              data.PermanencyGoal["@options"].replaceAll(
                                "'",
                                '"'
                              )
                            )[3],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          optionsOnNewLine={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end p-2 fw-bold">
                    {data.CaseworkerName["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="CaseworkerName"
                      error={errors.CaseworkerName}
                      value={getFieldValue(data.CaseworkerName)}
                      onChange={handleChange}
                      identical={compare("CaseworkerName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end p-2 fw-bold">
                    {data.ProviderSignature["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="ProviderSignature"
                      label={data.ProviderSignature["@label2"]}
                      error={errors.ProviderSignature}
                      value={getFieldValue(data.ProviderSignature)}
                      onChange={handleChange}
                      identical={compare("ProviderSignature")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      placeholder={data.ProviderSignature["@placeholder"]}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end p-2 fw-bold">
                    {data.FPName["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="FPName"
                      error={errors.FPName}
                      value={getFieldValue(data.FPName)}
                      onChange={handleChange}
                      identical={compare("FPName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end p-2 fw-bold">
                    {data.FPAddress["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="FPAddress"
                      error={errors.FPAddress}
                      value={getFieldValue(data.FPAddress)}
                      onChange={handleChange}
                      identical={compare("FPAddress")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end p-2 fw-bold">
                    {data.Section1BLabel["@label"]}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2">
                    <b>{data.CurrentDiagnosis["@label"]}</b>
                    <br />
                    <i>{data.CurrentDiagnosis["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="CurrentDiagnosis"
                      error={errors.CurrentDiagnosis}
                      value={getFieldValue(data.CurrentDiagnosis)}
                      onChange={handleChange}
                      identical={compare("CurrentDiagnosis")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-5 border-end p-2">
                    <b>{data.CurrentMedications["@label"]}</b>
                    <br />
                    <i>{data.CurrentMedications["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="CurrentMedications"
                      error={errors.CurrentMedications}
                      value={getFieldValue(data.CurrentMedications)}
                      onChange={handleChange}
                      identical={compare("CurrentMedications")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2 fw-bold">
                    {data.PrescribingProvider["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="PrescribingProvider"
                      error={errors.PrescribingProvider}
                      value={getFieldValue(data.PrescribingProvider)}
                      onChange={handleChange}
                      identical={compare("PrescribingProvider")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-5 border-end p-2">
                    <b>{data.MHSContent["@label"]}</b>
                    <br />
                    <i>{data.MHSContent["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="MHSContent"
                      error={errors.MHSContent}
                      value={getFieldValue(data.MHSContent)}
                      onChange={handleChange}
                      identical={compare("MHSContent")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-5 border-end p-2 fw-bold">
                    {data.TherapistName["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="TherapistName"
                      error={errors.TherapistName}
                      value={getFieldValue(data.TherapistName)}
                      onChange={handleChange}
                      identical={compare("TherapistName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-5 border-end p-2 fw-bold">
                    {data.AgencyName["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="AgencyName"
                      error={errors.AgencyName}
                      value={getFieldValue(data.AgencyName)}
                      onChange={handleChange}
                      identical={compare("AgencyName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-5 border-end p-2 fw-bold">
                    {data.TherapyDates["@label"]}
                    {!formDisabled ? (
                      <i
                        title="Refresh All Records"
                        className={`fas fa-sync fa-lg ms-2${
                          data.Date["@start"] === "" ||
                          data.Date["@end"] === "" ||
                          selectedProgramId === ""
                            ? " text-secondary"
                            : " text-body"
                        }`}
                        onClick={() => {
                          if (
                            data.Date["@start"] !== "" &&
                            data.Date["@end"] !== "" &&
                            selectedProgramId !== ""
                          ) {
                            convertRecordedServices();
                          }
                        }}
                      ></i>
                    ) : null}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="TherapyDates"
                      error={errors.TherapyDates}
                      value={getFieldValue(data.TherapyDates)}
                      onChange={handleChange}
                      identical={compare("TherapyDates")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom d-print-none">
                  <div className="col-5 border-end p-2">
                    <button
                      className="btn btn-link"
                      title="Open Encounter"
                      onClick={() => {
                        const encounterEnvironment =
                          process.env.REACT_APP_ENVIRONMENT === "Production"
                            ? "ccplive-cp"
                            : "d365base-cp";

                        handleCopyUrl(location.pathname).then(() => {
                          window.open(
                            `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                          );
                        });
                      }}
                    >
                      {data.EncounterLinkLabel["@label"]}
                    </button>
                  </div>
                  <div className="col border-end"></div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2">
                    <b>{data.TreatmentPlanProgress["@label"]}</b>
                    <br />
                    <i>{data.TreatmentPlanProgress["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="TreatmentPlanProgress"
                      error={errors.TreatmentPlanProgress}
                      value={getFieldValue(data.TreatmentPlanProgress)}
                      onChange={handleChange}
                      identical={compare("TreatmentPlanProgress")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2">
                    <b>{data.SchoolAdjustment["@label"]}</b>
                    <br />
                    <i>{data.SchoolAdjustment["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="SchoolAdjustment"
                      error={errors.SchoolAdjustment}
                      value={getFieldValue(data.SchoolAdjustment)}
                      onChange={handleChange}
                      identical={compare("SchoolAdjustment")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2 fw-bold">
                    {data.PeerRelations["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="PeerRelations"
                      error={errors.PeerRelations}
                      value={getFieldValue(data.PeerRelations)}
                      onChange={handleChange}
                      identical={compare("PeerRelations")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2">
                    <b>{data.HealthIssues["@label"]}</b>
                    <br />
                    <i>{data.HealthIssues["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="HealthIssues"
                      error={errors.HealthIssues}
                      value={getFieldValue(data.HealthIssues)}
                      onChange={handleChange}
                      identical={compare("HealthIssues")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2 fw-bold">
                    {data.PhysicalDetails["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="PhysicalDetails"
                      error={errors.PhysicalDetails}
                      value={getFieldValue(data.PhysicalDetails)}
                      onChange={handleChange}
                      identical={compare("PhysicalDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2 fw-bold">
                    {data.DentalDetails["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="DentalDetails"
                      error={errors.DentalDetails}
                      value={getFieldValue(data.DentalDetails)}
                      onChange={handleChange}
                      identical={compare("DentalDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2 fw-bold">
                    {data.VisionDetails["@label"]}
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="VisionDetails"
                      error={errors.VisionDetails}
                      value={getFieldValue(data.VisionDetails)}
                      onChange={handleChange}
                      identical={compare("VisionDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2">
                    <b>{data.CourtInvolvement["@label"]}</b>
                    <br />
                    <i>{data.CourtInvolvement["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="CourtInvolvement"
                      error={errors.CourtInvolvement}
                      value={getFieldValue(data.CourtInvolvement)}
                      onChange={handleChange}
                      identical={compare("CourtInvolvement")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2">
                    <b>{data.Safety["@label"]}</b>
                    <br />
                    <i>{data.Safety["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormTextArea
                      name="Safety"
                      error={errors.Safety}
                      value={getFieldValue(data.Safety)}
                      onChange={handleChange}
                      identical={compare("Safety")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2">
                    <b>{data.FamilyInvolvement["@label"]}</b>
                    <br />
                    <i>{data.FamilyInvolvement["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="FamilyInvolvement"
                      error={errors.FamilyInvolvement}
                      value={getFieldValue(data.FamilyInvolvement)}
                      onChange={handleChange}
                      identical={compare("FamilyInvolvement")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end p-2">
                    <b>{data.PermanencyPlanning["@label"]}</b>
                    <br />
                    <i>{data.PermanencyPlanning["@sub"]}</i>
                  </div>
                  <div className="col border-end p-2">
                    <FormInput
                      name="PermanencyPlanning"
                      error={errors.PermanencyPlanning}
                      value={getFieldValue(data.PermanencyPlanning)}
                      onChange={handleChange}
                      identical={compare("PermanencyPlanning")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end p-2 fw-bold">
                    {data.ChildAdjustmentSummary["@label"]}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end p-2">
                    <FormInput
                      name="ChildAdjustmentSummary"
                      error={errors.ChildAdjustmentSummary}
                      value={getFieldValue(data.ChildAdjustmentSummary)}
                      onChange={handleChange}
                      identical={compare("ChildAdjustmentSummary")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <FieldTable
                  name="ContactDetails"
                  commentsHandler={handleComments}
                >
                  <div className="row border-bottom bg-light">
                    <div className="col border-end p-2">
                      <b>{data.Section1CLabel["@label"]}</b>
                      {!formDisabled ? (
                        <i
                          title="Refresh All Records"
                          className={`fas fa-sync fa-lg ms-2${
                            data.Date["@start"] === "" ||
                            data.Date["@end"] === "" ||
                            selectedProgramId === ""
                              ? " text-secondary"
                              : " text-body"
                          }`}
                          onClick={() => {
                            if (
                              data.Date["@start"] !== "" &&
                              data.Date["@end"] !== "" &&
                              selectedProgramId !== ""
                            ) {
                              convertRecordedServices();
                            }
                          }}
                        ></i>
                      ) : null}
                      <button
                        className="btn btn-link d-print-none"
                        title="Open Encounter"
                        onClick={() => {
                          const encounterEnvironment =
                            process.env.REACT_APP_ENVIRONMENT === "Production"
                              ? "ccplive-cp"
                              : "d365base-cp";

                          handleCopyUrl(location.pathname).then(() => {
                            window.open(
                              `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                            );
                          });
                        }}
                      >
                        {data.EncounterLinkLabel["@label"]}
                      </button>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-3 border-end py-2 fw-bold">
                      {data.ContactDetails1stLabel["@label"]}
                    </div>
                    <div className="col-3 border-end py-2 fw-bold">
                      {data.ContactDetails2ndLabel["@label"]}
                    </div>
                    <div className="col border-end py-2 fw-bold">
                      {data.ContactDetails3rdLabel["@label"]}
                    </div>
                  </div>
                  {data.ContactDetails["@value"]
                    ? JSON.parse(data.ContactDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-3 border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(index, "ContactDetails");
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(index, "ContactDetails");
                                    }}
                                  ></i>
                                  <FormInput
                                    name="Date"
                                    value={item.Date}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "ContactDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                    type="date"
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "ContactDetails");
                                    }}
                                  ></i>
                                  {moment.isDate(item.Date)
                                    ? moment(item.Date).format("M/D/YYYY")
                                    : item.Date}
                                </>
                              )}
                            </div>
                            <div className="col-3 border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Location"
                                  value={item.Location}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "ContactDetails");
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Location
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Parties"
                                  value={item.Parties}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "ContactDetails");
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Parties
                              )}
                            </div>
                          </div>
                        )
                      )
                    : null}
                  {!formDisabled ? (
                    <div className="row border-bottom">
                      <div className="col border-end">
                        <button
                          className="btn btn-primary my-2"
                          onClick={() => {
                            handleAddRow("ContactDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          {data.ContactDetails["@addLabel"]}
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {errors.ContactDetails !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.ContactDetails}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <div className="row border-bottom bg-light">
                  <div className="col border-end py-2 fw-bold">
                    {data.Section2Label["@label"]}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end py-2 fw-bold">
                    {data.PrimaryCaretaker["@label"]}
                  </div>
                  <div className="col border-end py-2">
                    <FormInput
                      name="PrimaryCaretaker"
                      error={errors.PrimaryCaretaker}
                      value={getFieldValue(data.PrimaryCaretaker)}
                      onChange={handleChange}
                      identical={compare("PrimaryCaretaker")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end py-2 fw-bold">
                    {data.SecondaryCaretaker["@label"]}
                  </div>
                  <div className="col border-end py-2">
                    <FormInput
                      name="SecondaryCaretaker"
                      error={errors.SecondaryCaretaker}
                      value={getFieldValue(data.SecondaryCaretaker)}
                      onChange={handleChange}
                      identical={compare("SecondaryCaretaker")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end py-2 fw-bold">
                    {data.FormCompletedBy["@label"]}
                  </div>
                  <div className="col border-end py-2">
                    <FormInput
                      name="FormCompletedBy"
                      error={errors.FormCompletedBy}
                      value={getFieldValue(data.FormCompletedBy)}
                      onChange={handleChange}
                      identical={compare("FormCompletedBy")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end py-2 fw-bold">
                    {data.DateOfHomeVisit["@label"]}
                  </div>
                  <div className="col border-end py-2">
                    <FormInput
                      name="DateOfHomeVisit"
                      error={errors.DateOfHomeVisit}
                      value={getFieldValue(data.DateOfHomeVisit)}
                      onChange={handleChange}
                      identical={compare("DateOfHomeVisit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-4 border-end py-2 fw-bold">
                    {data.TimeOfHomeVisit["@label"]}
                  </div>
                  <div className="col border-end py-2">
                    <FormInput
                      name="TimeOfHomeVisit"
                      error={errors.TimeOfHomeVisit}
                      value={getFieldValue(data.TimeOfHomeVisit)}
                      onChange={handleChange}
                      identical={compare("TimeOfHomeVisit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-2 border-end fw-bold">
                    {data.VisitType["@label"]}
                  </div>
                  <div className="col border-end">
                    <div className="row border-bottom">
                      <div className="col py-2 fw-bold">
                        <FormRadioButton
                          name="VisitType"
                          error={errors.VisitType}
                          value={getFieldValue(data.VisitType)}
                          options={[
                            JSON.parse(
                              data.VisitType["@options"].replaceAll("'", '"')
                            )[0],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col py-2 fw-bold">
                        <FormRadioButton
                          name="VisitType"
                          error={errors.VisitType}
                          value={getFieldValue(data.VisitType)}
                          options={[
                            JSON.parse(
                              data.VisitType["@options"].replaceAll("'", '"')
                            )[1],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col border-end">
                    <div className="row border-bottom">
                      <div className="col py-2 fw-bold">
                        <FormRadioButton
                          name="VisitType"
                          error={errors.VisitType}
                          value={getFieldValue(data.VisitType)}
                          options={[
                            JSON.parse(
                              data.VisitType["@options"].replaceAll("'", '"')
                            )[2],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col py-2 fw-bold">
                        <FormRadioButton
                          name="VisitType"
                          error={errors.VisitType}
                          value={getFieldValue(data.VisitType)}
                          options={[
                            JSON.parse(
                              data.VisitType["@options"].replaceAll("'", '"')
                            )[3],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end py-2">
                    <b>{data.F2FContactWithLabel["@label"]}</b>{" "}
                    <i>{data.F2FContactWithLabel["@sub"]}</i>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end py-2 fw-bold">
                    {data.PrimaryCaretakerPresent["@label"]}
                  </div>
                  <div className="col-2 border-end py-2 fw-bold">
                    <FormRadioButton
                      name="PrimaryCaretakerPresent"
                      error={errors.PrimaryCaretakerPresent}
                      value={getFieldValue(data.PrimaryCaretakerPresent)}
                      options={[
                        JSON.parse(
                          data.PrimaryCaretakerPresent["@options"].replaceAll(
                            "'",
                            '"'
                          )
                        )[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col border-end py-2 fw-bold">
                    <FormRadioButton
                      name="PrimaryCaretakerPresent"
                      error={errors.PrimaryCaretakerPresent}
                      value={getFieldValue(data.PrimaryCaretakerPresent)}
                      options={[
                        JSON.parse(
                          data.PrimaryCaretakerPresent["@options"].replaceAll(
                            "'",
                            '"'
                          )
                        )[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end py-2 fw-bold">
                    {data.SecondaryCaretakerPresent["@label"]}
                  </div>
                  <div className="col-2 border-end py-2 fw-bold">
                    <FormRadioButton
                      name="SecondaryCaretakerPresent"
                      error={errors.SecondaryCaretakerPresent}
                      value={getFieldValue(data.SecondaryCaretakerPresent)}
                      options={[
                        JSON.parse(
                          data.SecondaryCaretakerPresent["@options"].replaceAll(
                            "'",
                            '"'
                          )
                        )[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col border-end py-2 fw-bold">
                    <FormRadioButton
                      name="SecondaryCaretakerPresent"
                      error={errors.SecondaryCaretakerPresent}
                      value={getFieldValue(data.SecondaryCaretakerPresent)}
                      options={[
                        JSON.parse(
                          data.SecondaryCaretakerPresent["@options"].replaceAll(
                            "'",
                            '"'
                          )
                        )[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end py-2 fw-bold">
                    {data.ChildPresent["@label"]}
                  </div>
                  <div className="col-2 border-end py-2 fw-bold">
                    <FormRadioButton
                      name="ChildPresent"
                      error={errors.ChildPresent}
                      value={getFieldValue(data.ChildPresent)}
                      options={[
                        JSON.parse(
                          data.ChildPresent["@options"].replaceAll("'", '"')
                        )[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col border-end py-2 fw-bold">
                    <FormRadioButton
                      name="ChildPresent"
                      error={errors.ChildPresent}
                      value={getFieldValue(data.ChildPresent)}
                      options={[
                        JSON.parse(
                          data.ChildPresent["@options"].replaceAll("'", '"')
                        )[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-4 border-end py-2">
                    <b>{data.OtherPresent["@label"]}</b>
                    {getFieldValue(data.OtherPresent) === "Yes" ? (
                      <FormInput
                        name="OtherPresentDetails"
                        label={data.OtherPresentDetails["@label"]}
                        error={errors.OtherPresentDetails}
                        value={getFieldValue(data.OtherPresentDetails)}
                        onChange={handleChange}
                        identical={compare("OtherPresentDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                  <div className="col-2 border-end py-2 fw-bold">
                    <FormRadioButton
                      name="OtherPresent"
                      error={errors.OtherPresent}
                      value={getFieldValue(data.OtherPresent)}
                      options={[
                        JSON.parse(
                          data.OtherPresent["@options"].replaceAll("'", '"')
                        )[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col border-end py-2 fw-bold">
                    <FormRadioButton
                      name="OtherPresent"
                      error={errors.OtherPresent}
                      value={getFieldValue(data.OtherPresent)}
                      options={[
                        JSON.parse(
                          data.OtherPresent["@options"].replaceAll("'", '"')
                        )[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end py-2">
                    <b>{data.SummaryOfChildInterview["@label"]}</b>{" "}
                    <i>{data.SummaryOfChildInterview["@sub"]}</i>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormTextArea
                      name="SummaryOfChildInterview"
                      error={errors.SummaryOfChildInterview}
                      value={getFieldValue(data.SummaryOfChildInterview)}
                      onChange={handleChange}
                      identical={compare("SummaryOfChildInterview")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end py-2">
                    <b>{data.VulnerabilitiesDetails["@label"]}</b>{" "}
                    <i>{data.VulnerabilitiesDetails["@sub"]}</i>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormCheckbox
                      name="VulnerabilitiesNone"
                      label={data.VulnerabilitiesNone["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesNone) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesCommunication"
                      label={data.VulnerabilitiesCommunication["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesCommunication) ===
                        "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesSelfProtection"
                      label={data.VulnerabilitiesSelfProtection["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesSelfProtection) ===
                        "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesAbuse"
                      label={data.VulnerabilitiesAbuse["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesAbuse) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesHelpers"
                      label={data.VulnerabilitiesHelpers["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesHelpers) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesAge"
                      label={data.VulnerabilitiesAge["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesAge) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesEmotionalNeeds"
                      label={data.VulnerabilitiesEmotionalNeeds["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesEmotionalNeeds) ===
                        "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesDevelopmental"
                      label={data.VulnerabilitiesDevelopmental["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesDevelopmental) ===
                        "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesAppearance"
                      label={data.VulnerabilitiesAppearance["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesAppearance) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesSpecialNeeds"
                      label={data.VulnerabilitiesSpecialNeeds["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesSpecialNeeds) ===
                        "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesVictimization"
                      label={data.VulnerabilitiesVictimization["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesVictimization) ===
                        "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesProvocative"
                      label={data.VulnerabilitiesProvocative["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesProvocative) ===
                        "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesResilience"
                      label={data.VulnerabilitiesResilience["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesResilience) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesRole"
                      label={data.VulnerabilitiesRole["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesRole) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="VulnerabilitiesVisibility"
                      label={data.VulnerabilitiesVisibility["@label"]}
                      checked={
                        getFieldValue(data.VulnerabilitiesVisibility) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormTextArea
                      name="VulnerabilitiesDetails"
                      error={errors.VulnerabilitiesDetails}
                      value={getFieldValue(data.VulnerabilitiesDetails)}
                      onChange={handleChange}
                      identical={compare("VulnerabilitiesDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end py-2">
                    <b>{data.ProtectionDetails["@label"]}</b>{" "}
                    <i>{data.ProtectionDetails["@sub"]}</i>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormCheckbox
                      name="ProtectionNeeds"
                      label={data.ProtectionNeeds["@label"]}
                      checked={getFieldValue(data.ProtectionNeeds) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="ProtectionIntervene"
                      label={data.ProtectionIntervene["@label"]}
                      checked={
                        getFieldValue(data.ProtectionIntervene) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="ProtectionBond"
                      label={data.ProtectionBond["@label"]}
                      checked={getFieldValue(data.ProtectionBond) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="ProtectionProvides"
                      label={data.ProtectionProvides["@label"]}
                      checked={
                        getFieldValue(data.ProtectionProvides) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="ProtectionResponds"
                      label={data.ProtectionResponds["@label"]}
                      checked={
                        getFieldValue(data.ProtectionResponds) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="ProtectionDiscipline"
                      label={data.ProtectionDiscipline["@label"]}
                      checked={
                        getFieldValue(data.ProtectionDiscipline) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="ProtectionPositive"
                      label={data.ProtectionPositive["@label"]}
                      checked={
                        getFieldValue(data.ProtectionPositive) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormTextArea
                      name="ProtectionDetails"
                      error={errors.ProtectionDetails}
                      value={getFieldValue(data.ProtectionDetails)}
                      onChange={handleChange}
                      identical={compare("ProtectionDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end py-2">
                    <b>{data.ChangesLabel["@label"]}</b>{" "}
                    <i>{data.ChangesLabel["@sub"]}</i>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesWillingness"
                      label={data.ChangesWillingness["@label"]}
                      checked={
                        getFieldValue(data.ChangesWillingness) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesWillingness) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesWillingnessDetails"
                        label={data.ChangesWillingnessDetails["@label"]}
                        error={errors.ChangesWillingnessDetails}
                        value={getFieldValue(data.ChangesWillingnessDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesWillingnessDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesAbility"
                      label={data.ChangesAbility["@label"]}
                      checked={getFieldValue(data.ChangesAbility) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesAbility) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesAbilityDetails"
                        label={data.ChangesAbilityDetails["@label"]}
                        error={errors.ChangesAbilityDetails}
                        value={getFieldValue(data.ChangesAbilityDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesAbilityDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesMarital"
                      label={data.ChangesMarital["@label"]}
                      checked={getFieldValue(data.ChangesMarital) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesMarital) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesMaritalDetails"
                        label={data.ChangesMaritalDetails["@label"]}
                        error={errors.ChangesMaritalDetails}
                        value={getFieldValue(data.ChangesMaritalDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesMaritalDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesHealth"
                      label={data.ChangesHealth["@label"]}
                      checked={getFieldValue(data.ChangesHealth) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesHealth) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesHealthDetails"
                        label={data.ChangesHealthDetails["@label"]}
                        error={errors.ChangesHealthDetails}
                        value={getFieldValue(data.ChangesHealthDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesHealthDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesPlacement"
                      label={data.ChangesPlacement["@label"]}
                      checked={getFieldValue(data.ChangesPlacement) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesPlacement) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesPlacementDetails"
                        label={data.ChangesPlacementDetails["@label"]}
                        error={errors.ChangesPlacementDetails}
                        value={getFieldValue(data.ChangesPlacementDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesPlacementDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesBirth"
                      label={data.ChangesBirth["@label"]}
                      checked={getFieldValue(data.ChangesBirth) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesBirth) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesBirthDetails"
                        label={data.ChangesBirthDetails["@label"]}
                        error={errors.ChangesBirthDetails}
                        value={getFieldValue(data.ChangesBirthDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesBirthDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesDeath"
                      label={data.ChangesDeath["@label"]}
                      checked={getFieldValue(data.ChangesDeath) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesDeath) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesDeathDetails"
                        label={data.ChangesDeathDetails["@label"]}
                        error={errors.ChangesDeathDetails}
                        value={getFieldValue(data.ChangesDeathDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesDeathDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesCrime"
                      label={data.ChangesCrime["@label"]}
                      checked={getFieldValue(data.ChangesCrime) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesCrime) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesCrimeDetails"
                        label={data.ChangesCrimeDetails["@label"]}
                        error={errors.ChangesCrimeDetails}
                        value={getFieldValue(data.ChangesCrimeDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesCrimeDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesMembers"
                      label={data.ChangesMembers["@label"]}
                      checked={getFieldValue(data.ChangesMembers) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesMembers) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesMembersDetails"
                        label={data.ChangesMembersDetails["@label"]}
                        error={errors.ChangesMembersDetails}
                        value={getFieldValue(data.ChangesMembersDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesMembersDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesRelocation"
                      label={data.ChangesRelocation["@label"]}
                      checked={getFieldValue(data.ChangesRelocation) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesRelocation) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesRelocationDetails"
                        label={data.ChangesRelocationDetails["@label"]}
                        error={errors.ChangesRelocationDetails}
                        value={getFieldValue(data.ChangesRelocationDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesRelocationDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesActivities"
                      label={data.ChangesActivities["@label"]}
                      checked={getFieldValue(data.ChangesActivities) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesActivities) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesActivitiesDetails"
                        label={data.ChangesActivitiesDetails["@label"]}
                        error={errors.ChangesActivitiesDetails}
                        value={getFieldValue(data.ChangesActivitiesDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesActivitiesDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesEmployment"
                      label={data.ChangesEmployment["@label"]}
                      checked={getFieldValue(data.ChangesEmployment) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesEmployment) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesEmploymentDetails"
                        label={data.ChangesEmploymentDetails["@label"]}
                        error={errors.ChangesEmploymentDetails}
                        value={getFieldValue(data.ChangesEmploymentDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesEmploymentDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom">
                  <div className="col-6 border-end py-2 fw-bold">
                    <FormCheckbox
                      name="ChangesRespite"
                      label={data.ChangesRespite["@label"]}
                      checked={getFieldValue(data.ChangesRespite) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  {getFieldValue(data.ChangesRespite) === "true" ? (
                    <div className="col border-end py-2">
                      <FormInput
                        name="ChangesRespiteDetails"
                        label={data.ChangesRespiteDetails["@label"]}
                        error={errors.ChangesRespiteDetails}
                        value={getFieldValue(data.ChangesRespiteDetails)}
                        onChange={handleChange}
                        identical={compare("ChangesRespiteDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end py-2 fw-bold">
                    {data.SupportiveServicesNeeded["@label"]}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormTextArea
                      name="SupportiveServicesNeeded"
                      error={errors.SupportiveServicesNeeded}
                      value={getFieldValue(data.SupportiveServicesNeeded)}
                      onChange={handleChange}
                      identical={compare("SupportiveServicesNeeded")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom bg-light">
                  <div className="col border-end py-2 fw-bold">
                    {data.CasePlanProgress["@label"]}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormTextArea
                      name="CasePlanProgress"
                      error={errors.CasePlanProgress}
                      value={getFieldValue(data.CasePlanProgress)}
                      onChange={handleChange}
                      identical={compare("CasePlanProgress")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <div className="mt-2 pt-2 border-top">
              Please be reminded that you are welcome to provide information and
              feedback at any time and you are always welcome to join us at
              meetings regarding any SAFY youth on your caseload.
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default OHTFCSORCMonthlyProgressReport;
